import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/pages/DashboardLayout";
import Overview from "@/pages/app/Overview";
import Workloads from "@/pages/app/Workloads";
import Agents from "@/pages/app/Agents";
import Vault from "@/pages/app/Vault";
import Policies from "@/pages/app/Policies";
import Threats from "@/pages/app/Threats";
import Compliance from "@/pages/app/Compliance";
import AIInsights from "@/pages/app/AIInsights";
import Leads from "@/pages/app/Leads";
import Billing from "@/pages/app/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Resources from "@/pages/Resources";
import SeoLanding from "@/pages/SeoLanding";
import VendorCompare from "@/pages/VendorCompare";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span className="font-mono text-xs text-muted-foreground tracking-widest">INITIALIZING SECURE SESSION…</span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  return user.role === "admin" ? children : <Navigate to="/app" replace />;
}

function RoutedApp() {
  const location = useLocation();
  // Process Google OAuth return (session_id in URL fragment) before any route/guard runs.
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/resources" element={<Resources />} />
      <Route path="/compare/acronis-alternative-pharma" element={<SeoLanding slug="acronis-alternative-pharma" />} />
      <Route path="/compare/backup-vendors" element={<VendorCompare />} />
      <Route path="/guides/21-cfr-part-11-backup-requirements" element={<SeoLanding slug="21-cfr-part-11-backup-requirements" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route
        path="/app"
        element={
          <Protected>
            <DashboardLayout />
          </Protected>
        }
      >
        <Route index element={<Overview />} />
        <Route path="workloads" element={<Workloads />} />
        <Route path="agents" element={<Agents />} />
        <Route path="vault" element={<Vault />} />
        <Route path="policies" element={<Policies />} />
        <Route path="threats" element={<Threats />} />
        <Route path="compliance" element={<Compliance />} />
        <Route path="ai" element={<AIInsights />} />
        <Route path="leads" element={<AdminOnly><Leads /></AdminOnly>} />
        <Route path="billing" element={<AdminOnly><Billing /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" />
          <RoutedApp />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
